import { ref } from 'vue'
import { defineStore } from 'pinia'
import type { AxiosError, AxiosResponse } from 'axios'
import type { UserProfile } from '@/types/user'
import { fetchUserProfile } from '@/features/auth/api/profile'
import { useTokenStore } from './useTokenStore'

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error
  }

  if (error instanceof Error) {
    return error.message
  }

  if (error && typeof error === 'object' && 'toString' in error) {
    return String(error)
  }

  return 'Unknown error'
}

export const useUserStore = defineStore('user', () => {
  const name = ref<string | null>(null)
  const email = ref<string | null>(null)
  const avatarUrl = ref<string | null>(null)
  const tempEmailForPasswordReset = ref<string | null>(null)

  const error = ref<string | null>(null)
  const loader = ref(false)

  const setUserInfo = (userName: string, userEmail: string) => {
    name.value = userName
    email.value = userEmail
    // Persist username for token refresh (survives page reload)
    useTokenStore().persistUsername(userName)
  }

  const setTempEmailForPasswordReset = (emailAddress: string) => {
    tempEmailForPasswordReset.value = emailAddress
  }

  const getUserProfile = async (): Promise<AxiosResponse<UserProfile>> => {
    error.value = null
    loader.value = true

    try {
      const tokenStore = useTokenStore()

      if (!tokenStore.accessToken) {
        throw new Error('Missing access token')
      }

      const response = await fetchUserProfile()

      return response
    } catch (err: unknown) {
      const axiosError = err as AxiosError<{ message?: string }> | Error | string
      error.value = getErrorMessage(axiosError)
      throw err
    } finally {
      loader.value = false
    }
  }

  const clearUserInfo = () => {
    name.value = null
    email.value = null
    avatarUrl.value = null
    tempEmailForPasswordReset.value = null
  }

  const clearUserPreferences = () => {
    avatarUrl.value = null
  }

  const isLoggedIn = () => {
    const tokenStore = useTokenStore()
    return tokenStore.isAuthenticated()
  }

  return {
    name,
    email,
    avatarUrl,
    tempEmailForPasswordReset,
    error,
    loader,
    setUserInfo,
    setTempEmailForPasswordReset,
    getUserProfile,
    clearUserInfo,
    clearUserPreferences,
    isLoggedIn
  }
})

